import { useEffect, useState } from "react";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_ticket_issue_types } from "../../../constants";
import {
  ISSUE_TYPE_OPTIONS,
  CATEGORY_ISSUE_TYPE_MAP,
  PRIORITY_OPTIONS,
  MENU_OPTIONS,
  getDefaultStatusOptions,
} from "./MaintenanceBadges";

// The issue types and priority labels for one store and ticket category.
// Companies can rename both, so until the fetch answers the built-in wording
// may be wrong: `ticketOptionsStatus` is "loading" | "ready" | "error" and
// the ticket drawer stays read-only while it is not "ready". `enabled` lets
// a surface that only sometimes opens the drawer skip the fetch until then.
export default function useTicketOptions(homeStore, category, enabled = true) {
  const [issueTypeOptions, setIssueTypeOptions] = useState(
    CATEGORY_ISSUE_TYPE_MAP[category] || ISSUE_TYPE_OPTIONS
  );
  const [priorityOptions, setPriorityOptions] = useState(PRIORITY_OPTIONS);
  // Status wording and menu choices follow the category; both fall back to
  // built-in defaults and never gate readiness.
  const [statusOptions, setStatusOptions] = useState(getDefaultStatusOptions(category));
  const [menuOptions, setMenuOptions] = useState(MENU_OPTIONS);
  const [ticketOptionsStatus, setTicketOptionsStatus] = useState("loading");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    // Immediately set defaults so stale options from the previous category aren't visible
    setIssueTypeOptions(CATEGORY_ISSUE_TYPE_MAP[category] || ISSUE_TYPE_OPTIONS);
    setPriorityOptions(PRIORITY_OPTIONS);
    setStatusOptions(getDefaultStatusOptions(category));
    setMenuOptions(MENU_OPTIONS);
    setTicketOptionsStatus("loading");
    if (!enabled || !homeStore) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_ticket_issue_types, {
          store_id: homeStore,
          category,
        });
        if (cancelled) return;
        const data = res.data?.redux?.payload?.issue_types;
        if (data?.length) setIssueTypeOptions(data);
        const priorities = res.data?.redux?.payload?.priorities;
        if (priorities?.length) setPriorityOptions(priorities);
        const statuses = res.data?.redux?.payload?.statuses;
        if (statuses?.length) setStatusOptions(statuses);
        const menus = res.data?.redux?.payload?.menu_options;
        if (menus?.length) setMenuOptions(menus);
        // A backend still on the previous release answers without
        // ``priorities``; treat that like a failed fetch so the built-in
        // wording is never presented as the company's.
        setTicketOptionsStatus(priorities?.length ? "ready" : "error");
      } catch {
        if (!cancelled) setTicketOptionsStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [homeStore, category, enabled, retryKey]);

  return {
    issueTypeOptions,
    priorityOptions,
    statusOptions,
    menuOptions,
    ticketOptionsStatus,
    retryTicketOptions: () => setRetryKey((k) => k + 1),
  };
}
